const PLACEHOLDER_IMAGE = "https://placehold.co/900x900/e7f4f6/007399?text=CJ";
const { parseProductDropshippingVariantFilters } = require("./productDropshippingPreviewVariants");

const numberValue = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const uniqueImages = (values = []) => {
  const seen = new Set();
  return values.flat(Infinity).reduce((images, value) => {
    const url = typeof value === "string" ? value.trim() : "";
    if (!url || seen.has(url)) return images;
    seen.add(url);
    images.push(url);
    return images;
  }, []);
};

const normalizeFilterKey = (value) => String(value || "").trim().toLowerCase().replace(/[\s_-]+/g, " ");
const normalizeFilterValue = (value) => String(value ?? "").trim().toLowerCase();

const variantFilterObject = (variant) => {
  const source = variant?.filters && typeof variant.filters === "object" && !Array.isArray(variant.filters)
    ? variant.filters
    : variant?.attributes && typeof variant.attributes === "object" && !Array.isArray(variant.attributes)
      ? variant.attributes
    : {};
  const filters = Object.entries(source).reduce((result, [key, value]) => {
    const resolvedValue = Array.isArray(value) ? value[0] : value;
    if (resolvedValue !== undefined && resolvedValue !== null && String(resolvedValue).trim()) {
      result[key] = String(resolvedValue).trim();
    }
    return result;
  }, {});
  return Object.keys(filters).length ? filters : { Options: variant?.title || variant?.sku || variant?.id };
};

const buildVariantFilterGroups = (variants = []) => {
  const groups = new Map();
  variants.forEach((variant) => {
    Object.entries(variantFilterObject(variant)).forEach(([label, value]) => {
      const key = normalizeFilterKey(label);
      if (!groups.has(key)) groups.set(key, { key, label, values: [] });
      const group = groups.get(key);
      if (!group.values.some((entry) => normalizeFilterValue(entry) === normalizeFilterValue(value))) group.values.push(value);
    });
  });
  return [...groups.values()];
};

const variantMatchesFilters = (variant, selectedFilters = {}) => {
  const variantFilters = variantFilterObject(variant);
  return Object.entries(selectedFilters).every(([selectedKey, selectedValue]) =>
    Object.entries(variantFilters).some(([variantKey, variantValue]) =>
      normalizeFilterKey(variantKey) === normalizeFilterKey(selectedKey)
      && normalizeFilterValue(variantValue) === normalizeFilterValue(selectedValue)
    )
  );
};

const selectVariantForFilter = (variants = [], currentFilters = {}, key, value) => {
  const nextFilters = { ...currentFilters, [key]: value };
  let match = variants.find((variant) => variant.stock > 0 && variantMatchesFilters(variant, nextFilters));
  if (!match) match = variants.find((variant) => variant.stock > 0 && variantMatchesFilters(variant, { [key]: value }));
  if (!match) return null;
  return { variant: match, selectedFilters: variantFilterObject(match) };
};

const normalizeCJProductPreviewData = (product) => {
  if (!product || typeof product !== "object") return null;
  const images = uniqueImages(product.images);
  const variants = (Array.isArray(product.variants) ? product.variants : []).map((variant, index) => {
    const filters = parseProductDropshippingVariantFilters(product.title || product.name, variant);
    return {
      id: String(variant?.externalVariantId || variant?.sku || index),
      externalVariantId: variant?.externalVariantId || "",
      title: variant?.name || variant?.sku || `Variant ${index + 1}`,
      sku: variant?.sku || "",
      price: numberValue(variant?.suggestedSalePrice || variant?.costPrice),
      costPrice: numberValue(variant?.costPrice),
      stock: numberValue(variant?.stockQuantity),
      images: uniqueImages([variant?.image, variant?.images]),
      attributes: filters,
      filters,
      raw: variant,
    };
  });

  return {
    id: String(product._id || product.externalProductId || ""),
    title: product.title || product.name || "Product",
    description: product.description || "",
    price: numberValue(product.suggestedSalePrice || product.price || product.costPrice),
    costPrice: numberValue(product.costPrice),
    currency: product.currency || "USD",
    stock: numberValue(product.stockQuantity),
    sku: product.sku || "",
    images: images.length ? images : [PLACEHOLDER_IMAGE],
    variants,
    supplier: product.supplierName || product.brand || "CJ Dropshipping",
    availability: product.availability || "unknown",
    sourceType: "dropshipping",
    sourceProductId: product.externalProductId || "",
    raw: product,
  };
};

const getPreviewForVariant = (preview, variantId) => {
  if (!preview) return null;
  const selectedVariant = preview.variants.find((variant) => variant.id === String(variantId || "")) || null;
  return {
    ...preview,
    selectedVariant,
    title: selectedVariant?.title || preview.title,
    price: selectedVariant?.price || preview.price,
    costPrice: selectedVariant?.costPrice || preview.costPrice,
    stock: selectedVariant ? selectedVariant.stock : preview.stock,
    sku: selectedVariant?.sku || preview.sku,
    images: selectedVariant
      ? uniqueImages([selectedVariant.images, preview.images])
      : preview.images,
  };
};

module.exports = {
  PLACEHOLDER_IMAGE,
  uniqueImages,
  normalizeCJProductPreviewData,
  getPreviewForVariant,
  variantFilterObject,
  buildVariantFilterGroups,
  variantMatchesFilters,
  selectVariantForFilter,
};
